import type { IPortfolioMetadata } from "@pulse-ui/codegen/generated_ts/org_metadata";
import { api } from "../base";

interface OrgResponse {
  data?: { org_id: string }[];
}

interface OrgMetadataResponse {
  portfolios?: IPortfolioMetadata[];
}

const portfolioEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Two-step fetch: GET /orgs → GET /orgs/{id}/metadata.
     * Returns the full portfolio list with nested accounts.
     */
    getUserPortfolios: build.query<IPortfolioMetadata[], void>({
      async queryFn(_arg, _api, _extraOptions, baseQuery) {
        const orgsResult = await baseQuery({ url: "/api/account/v1/orgs" });
        if (orgsResult.error) return { error: orgsResult.error };

        const orgs = orgsResult.data as OrgResponse;
        const orgId = orgs?.data?.[0]?.org_id;
        if (!orgId) return { data: [] };

        const metaResult = await baseQuery({
          url: `/api/account/v1/orgs/${orgId}/metadata`,
        });
        if (metaResult.error) return { error: metaResult.error };

        const meta = metaResult.data as OrgMetadataResponse;
        return { data: (meta?.portfolios ?? []) as IPortfolioMetadata[] };
      },
      providesTags: ["UserPortfolios"],
    }),

    /** Fetches available pricing assets (e.g. ["USDT", "BTC", "ETH"]). */
    getPricingAssets: build.query<string[], void>({
      query: () => ({ url: "/api/portfolio/v1/pricing_assets" }),
      transformResponse: (response: string[] | { data: string[] }) =>
        Array.isArray(response) ? response : (response.data ?? []),
    }),
  }),
});

export const { useGetUserPortfoliosQuery, useGetPricingAssetsQuery } =
  portfolioEndpoints;
